import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Grid,
  FormControl,
  FormLabel,
  TextField,
  Select,
  MenuItem,
  Stack,
  Chip,
  Tooltip,
  InputAdornment,
  CircularProgress,
  Button,
  Input,
  IconButton
} from '@mui/material';
import {
  Person as PersonIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { FieldArray, ErrorMessage } from 'formik';
import { toast } from 'react-toastify';

import { ID_CARD_TYPES, ID_CARD_TYPE_LABELS, emptyCustomer } from './constants';
import { GRADIENT_PRIMARY, SHADOW_CARD } from './theme';
import { lookupCustomerByIdCard, formatBirthDateForInput } from './services/customerLookup';

const PHONE_MAX_LENGTH = 10;
const ID_CARD_MIN_LENGTH = 5;

const CustomerDetailsStep = ({
  values,
  setFieldValue,
  isSearching,
  fetchCustomerData,
  existingCustomerData,
  hotel
}) => (
  <Card sx={{ mb: 3, boxShadow: SHADOW_CARD }}>
    <Box sx={{ background: GRADIENT_PRIMARY, p: 2, color: 'white' }}>
      <Box display="flex" alignItems="center" gap={2}>
        <PersonIcon />
        <Typography variant="h6" fontWeight="bold">
          Información del Huésped
        </Typography>
      </Box>
    </Box>

    <CardContent sx={{ p: 3 }}>
      <FieldArray
        name="customers"
        render={(arrayHelpers) => (
          <Stack spacing={3}>
            {values?.customers?.map((customer, index) => (
              <CustomerCard
                key={index}
                index={index}
                customer={customer}
                values={values}
                setFieldValue={setFieldValue}
                isSearching={isSearching}
                fetchCustomerData={fetchCustomerData}
                existingCustomerData={existingCustomerData}
                hotel={hotel}
                onRemove={() => arrayHelpers.remove(index)}
              />
            ))}

            <Button
              type="button"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => arrayHelpers.push(emptyCustomer())}
              size="large"
              sx={{ py: 1.5, borderStyle: 'dashed', borderWidth: 2 }}
            >
              Agregar Otro Huésped
            </Button>
          </Stack>
        )}
      />
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------------------
// CustomerCard — encapsula un huésped del FieldArray.
// ---------------------------------------------------------------------------

const CustomerCard = ({
  index,
  customer,
  values,
  setFieldValue,
  isSearching,
  fetchCustomerData,
  existingCustomerData,
  hotel,
  onRemove
}) => {
  const setField = (field, value) => setFieldValue(`customers.${index}.${field}`, value);

  const handlePhoneChange = (raw) => {
    const phoneNumber = raw.replace(/\D/g, '').slice(0, PHONE_MAX_LENGTH);
    setField('phoneNumber', phoneNumber);
    setField('idFile', '');
  };

  const handlePhoneSearch = async () => {
    if (customer.phoneNumber.length !== PHONE_MAX_LENGTH) return;
    const data = await fetchCustomerData(customer.phoneNumber);

    if (!data) {
      toast.info('ℹ️ Cliente nuevo - Complete los datos');
      return;
    }

    const updated = [...values.customers];
    updated[index] = {
      phoneNumber: customer.phoneNumber,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      idCardType: data.idCardType || ID_CARD_TYPES.DEFAULT,
      idcardNumber: data.idcardNumber || '',
      address: data.address || '',
      idFile: data.idFile || ''
    };
    setFieldValue('customers', updated);
    toast.success('✅ Cliente encontrado');
  };

  const handleIdCardBlur = (raw) => {
    const val = raw.trim();
    if (customer.idCardType === ID_CARD_TYPES.ID && /^[0-9]+$/.test(val)) {
      setField('idcardNumber', `V-${val}`);
    }
  };

  const handleIdCardSearch = async () => {
    if (!customer.idcardNumber || customer.idcardNumber.length < ID_CARD_MIN_LENGTH) return;

    const data = await lookupCustomerByIdCard(customer.idcardNumber, hotel?.hotelId);
    if (!data) {
      toast.info('ℹ️ Cliente nuevo');
      return;
    }

    // Los `|| ''` evitan poner undefined/null en Formik. Un email o cédula
    // en formato viejo desde la BD igualmente fallará la validación Yup, pero
    // ahora el paso de pago lista esos errores en vez de bloquear en silencio.
    setField('phoneNumber', data.phoneNumber || '');
    setField('firstName', data.firstName || '');
    setField('lastName', data.lastName || '');
    setField('email', data.email || '');
    setField('idCardType', data.idCardType || ID_CARD_TYPES.DEFAULT);
    setField('address', data.address || '');
    setField('idFile', data.idFile || '');
    if (data.birthDate) setField('birthDate', formatBirthDateForInput(data.birthDate));

    toast.success(data.isExternal ? '✅ Datos encontrados en registro civil' : '✅ Cliente encontrado');
  };

  return (
    <Card
      variant="outlined"
      sx={{
        p: 3,
        bgcolor: '#fafafa',
        border: '2px solid #e0e0e0',
        borderRadius: 2,
        position: 'relative'
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Chip icon={<PersonIcon />} label={`Huésped ${index + 1} `} color="primary" variant="outlined" />
        {index > 0 && (
          <Tooltip title="Eliminar Huésped">
            <IconButton color="error" size="small" onClick={onRemove}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Teléfono con búsqueda */}
        <Grid item xs={12} md={6}>
          <SearchableField
            label={
              <>
                <PhoneIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                Número de Teléfono
              </>
            }
            name={`customers.${index}.phoneNumber`}
            value={customer.phoneNumber}
            placeholder="Ingrese 10 dígitos"
            tooltip="Buscar cliente existente"
            isSearching={isSearching}
            searchDisabled={
              !customer.phoneNumber || customer.phoneNumber.length < PHONE_MAX_LENGTH || isSearching
            }
            onChange={(e) => handlePhoneChange(e.target.value)}
            onSearch={handlePhoneSearch}
          />
        </Grid>

        {/* Identificación con búsqueda */}
        <Grid item xs={12} md={6}>
          <SearchableField
            label={
              <>
                <BadgeIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                Número de Identificación
              </>
            }
            name={`customers.${index}.idcardNumber`}
            value={customer.idcardNumber || ''}
            placeholder="V-12345678 o pasaporte"
            tooltip="Buscar por identificación"
            isSearching={isSearching}
            searchDisabled={
              !customer.idcardNumber || customer.idcardNumber.length < 8 || isSearching
            }
            onChange={(e) => setField('idcardNumber', e.target.value)}
            onBlur={(e) => handleIdCardBlur(e.target.value)}
            onSearch={handleIdCardSearch}
          />
        </Grid>

        <SimpleField
          label="Nombre"
          name={`customers.${index}.firstName`}
          value={customer.firstName || ''}
          placeholder="Nombre del huésped"
          onChange={(e) => setField('firstName', e.target.value)}
        />

        <SimpleField
          label="Apellido"
          name={`customers.${index}.lastName`}
          value={customer.lastName || ''}
          placeholder="Apellido del huésped"
          onChange={(e) => setField('lastName', e.target.value)}
        />

        <SimpleField
          label={
            <>
              <EmailIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
              Correo Electrónico
            </>
          }
          name={`customers.${index}.email`}
          value={customer.email || ''}
          placeholder="correo@ejemplo.com"
          onChange={(e) => setField('email', e.target.value)}
        />

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>Tipo de Identificación</FormLabel>
            <Select
              name={`customers.${index}.idCardType`}
              size="small"
              fullWidth
              value={customer.idCardType || ID_CARD_TYPES.DEFAULT}
              onChange={(e) => setField('idCardType', e.target.value)}
              sx={{ bgcolor: 'white' }}
            >
              <MenuItem value={ID_CARD_TYPES.DEFAULT} disabled>
                {ID_CARD_TYPE_LABELS[ID_CARD_TYPES.DEFAULT]}
              </MenuItem>
              {Object.values(ID_CARD_TYPES)
                .filter((t) => t !== ID_CARD_TYPES.DEFAULT)
                .map((t) => (
                  <MenuItem key={t} value={t}>
                    {ID_CARD_TYPE_LABELS[t]}
                  </MenuItem>
                ))}
            </Select>
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              <ErrorMessage name={`customers.${index}.idCardType`} />
            </Typography>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>Fecha de Nacimiento</FormLabel>
            <TextField
              name={`customers.${index}.birthDate`}
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formatBirthDateForInput(customer.birthDate)}
              onChange={(e) => setField('birthDate', e.target.value)}
              sx={{ bgcolor: 'white' }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
              <HomeIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
              Dirección
            </FormLabel>
            <TextField
              name={`customers.${index}.address`}
              size="small"
              fullWidth
              multiline
              rows={2}
              placeholder="Dirección completa del huésped"
              value={customer.address || ''}
              onChange={(e) => setField('address', e.target.value)}
              sx={{ bgcolor: 'white' }}
            />
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              <ErrorMessage name={`customers.${index}.address`} />
            </Typography>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <IdFileField
            index={index}
            customer={customer}
            existingCustomerData={existingCustomerData}
            onChange={(file) => setField('idFile', file)}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Subcomponentes reutilizables
// ---------------------------------------------------------------------------

const SearchableField = ({
  label,
  name,
  value,
  placeholder,
  tooltip,
  isSearching,
  searchDisabled,
  onChange,
  onBlur,
  onSearch
}) => (
  <FormControl fullWidth>
    <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>{label}</FormLabel>
    <TextField
      name={name}
      size="small"
      fullWidth
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={tooltip}>
              <span>
                <IconButton edge="end" disabled={searchDisabled} onClick={onSearch} color="primary">
                  {isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </InputAdornment>
        ),
        sx: { bgcolor: 'white' }
      }}
    />
    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
      <ErrorMessage name={name} />
    </Typography>
  </FormControl>
);

const SimpleField = ({ label, name, value, placeholder, onChange }) => (
  <Grid item xs={12} md={6}>
    <FormControl fullWidth>
      <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>{label}</FormLabel>
      <TextField
        name={name}
        size="small"
        fullWidth
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        sx={{ bgcolor: 'white' }}
      />
      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
        <ErrorMessage name={name} />
      </Typography>
    </FormControl>
  </Grid>
);

const IdFileField = ({ index, customer, existingCustomerData, onChange }) => {
  const hasSavedFile =
    existingCustomerData?.idFile ||
    (customer.idFile && !(customer.idFile instanceof File));

  return (
    <FormControl fullWidth>
      <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>📄 Documento de Identidad</FormLabel>
      {hasSavedFile ? (
        <Button
          startIcon={<VisibilityIcon />}
          variant="outlined"
          component="a"
          href={customer.idFile}
          target="_blank"
          rel="noreferrer"
          fullWidth
          sx={{ justifyContent: 'flex-start' }}
        >
          Ver Documento Guardado
        </Button>
      ) : (
        <Box
          sx={{
            border: '2px dashed #e0e0e0',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            bgcolor: 'white',
            '&:hover': { borderColor: 'primary.main', bgcolor: '#f5f5f5' }
          }}
        >
          <Input
            id={`customers.${index}.idFile`}
            name={`customers.${index}.idFile`}
            type="file"
            onChange={(event) => onChange(event.currentTarget.files[0])}
            inputProps={{ accept: 'image/*,.pdf' }}
            sx={{ width: '100%' }}
          />
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Formatos aceptados: Imágenes y PDF
          </Typography>
        </Box>
      )}
      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
        <ErrorMessage name={`customers.${index}.idFile`} />
      </Typography>
    </FormControl>
  );
};

export default CustomerDetailsStep;
